import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import { Calendar, ArrowRight } from 'lucide-react';

const Adventures = () => {
  const [adventures, setAdventures] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadData = async () => {
      const data = await fetchCollection('adventures');
      
      // Helper to parse Danish date strings like "13. maj 2024"
      const parseDanishDate = (dateStr) => {
        if (!dateStr) return new Date(0);
        
        const months = {
          'januar': 0, 'jan': 0,
          'februar': 1, 'feb': 1,
          'marts': 2, 'mar': 2,
          'april': 3, 'apr': 3,
          'maj': 4,
          'juni': 5, 'jun': 5,
          'juli': 6, 'jul': 6,
          'august': 7, 'aug': 7,
          'september': 8, 'sep': 8,
          'oktober': 9, 'okt': 9,
          'november': 10, 'nov': 10,
          'december': 11, 'dec': 11
        };

        const parts = dateStr.toLowerCase().replace('.', '').split(' ');
        if (parts.length !== 3) return new Date(0);

        const day = parseInt(parts[0], 10);
        const month = months[parts[1]];
        const year = parseInt(parts[2], 10);

        if (isNaN(day) || month === undefined || isNaN(year)) return new Date(0);
        
        return new Date(year, month, day);
      };

      // Sort by the 'date' field descending (newest first)
      const sortedData = [...data].sort((a, b) => {
        const dateA = parseDanishDate(a.date);
        const dateB = parseDanishDate(b.date);
        
        // If dates are equal, fallback to updatedAt
        if (dateB.getTime() === dateA.getTime()) {
          return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
        }
        
        return dateB.getTime() - dateA.getTime();
      });

      setAdventures(sortedData);
      setLoading(false);
    };
    loadData();
  }, []);

  return (
    <>
      <Helmet>
        <title>Mine Eventyr | LimfjordsOdderen</title>
        <meta name="description" content="Læs de nyeste historier fra mit liv i Limfjorden. Fra sild-fester til nærkampe med måger." />
      </Helmet>

      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">Mine Eventyr</h1>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Her deler jeg mine vildeste oplevelser fra fjorden. Det er ikke altid kedeligt at være en odder!
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {adventures.map((adv) => (
                <article key={adv.id} className="group bg-blue-50 rounded-3xl overflow-hidden hover:shadow-2xl transition-all duration-300 flex flex-col">
                  <div className="aspect-video overflow-hidden">
                    <img 
                      src={adv.image[0]} 
                      alt={adv.title.da}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-8 flex-grow flex flex-col">
                    <div className="flex items-center gap-2 text-blue-600 text-sm font-medium mb-4">
                      <Calendar className="h-4 w-4" />
                      {adv.date}
                    </div>
                    <h2 className="text-2xl font-bold text-slate-900 mb-4 group-hover:text-blue-600 transition-colors">
                      {adv.title.da}
                    </h2>
                    <div 
                      className="text-slate-600 mb-6 line-clamp-3"
                      dangerouslySetInnerHTML={{ __html: adv.content.da }}
                    />
                    <div className="mt-auto">
                      <Link 
                        to={`/eventyr/${adv.slug.da}`}
                        className="inline-flex items-center gap-2 font-bold text-blue-600 hover:text-blue-700 transition-colors"
                      >
                        Læs hele historien <ArrowRight className="h-4 w-4" />
                      </Link>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Adventures;
